/**
 * A tournament as it appears on the home page. The card closes on a scoreboard
 * strip — divisions on the left, team count on the right — because those two
 * numbers are what a coach actually needs to decide whether to keep reading.
 */
import { Link } from "react-router-dom";
import { stateName } from "@alpha/shared";
import type { TournamentListItem } from "../lib/types";
import { formatDateRange, registrationOpen } from "../lib/format";
import { divisionMatches } from "../lib/tournamentSearch";
import { tournamentPath } from "../lib/routes";
import { TournamentBanner } from "./TournamentBanner";
import { Badge, CalendarIcon, Card, ChevronRightIcon, MapPinIcon, cx } from "./ui";

/**
 * Venue names when the organizer has attached them, else the free-text city.
 * City and state are set independently, so fall back to the state name rather
 * than showing nothing for an event that the state filter can already find.
 */
function whereLine(t: TournamentListItem): string {
  const venues = t.summary.venues.map((v) => v.name);
  if (venues.length > 0) return venues.join(" · ");
  return t.location || stateName(t.state);
}

/** Fixed strip height so cards in a row line up whatever their content. */
const stripCls = "flex min-h-[2.75rem] items-center gap-3 bg-navy-950 px-5 py-3";

function Scoreboard({ t, terms }: { t: TournamentListItem; terms: string[] }) {
  const { divisions, teamCount } = t.summary;

  if (divisions.length === 0 && teamCount === 0) {
    return <div className={cx(stripCls, "text-xs text-slate-400")}>Divisions to be announced</div>;
  }

  return (
    <div className={cx(stripCls, "justify-between")}>
      <ul className="flex min-w-0 flex-wrap items-center gap-1">
        {divisions.map((d) => (
          <li
            key={d}
            className={cx(
              "rounded-md px-1.5 py-0.5 font-display text-[11px] font-semibold tracking-wide",
              divisionMatches(d, terms)
                ? "bg-brand-400 text-navy-950"
                : "bg-white/10 text-slate-200",
            )}
          >
            {d}
          </li>
        ))}
      </ul>
      <div className="flex shrink-0 items-baseline gap-1.5">
        <span className="font-display text-sm font-bold tabular-nums text-white">{teamCount}</span>
        <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
          {teamCount === 1 ? "team" : "teams"}
        </span>
      </div>
    </div>
  );
}

export function TournamentCard({ t, terms }: { t: TournamentListItem; terms: string[] }) {
  const where = whereLine(t);

  return (
    <Link to={tournamentPath(t.id)} className="group block h-full">
      <Card hover className="flex h-full flex-col overflow-hidden">
        <TournamentBanner name={t.name} seed={t.id} src={t.bannerImageUrl} size="card" />
        <div className="flex flex-1 flex-col gap-3 p-5">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-display text-lg font-semibold tracking-tight text-navy-950 transition-colors group-hover:text-brandText">
              {t.name}
            </h3>
            <ChevronRightIcon className="h-4 w-4 shrink-0 text-slate-300 transition-all duration-150 group-hover:translate-x-0.5 group-hover:text-brand-600" />
          </div>
          <div className="space-y-1.5 text-sm text-slate-500">
            <p className="flex items-center gap-1.5">
              <CalendarIcon className="h-4 w-4 shrink-0 text-slate-400" />
              {formatDateRange(t.startDate, t.endDate)}
            </p>
            {where ? (
              <p className="flex min-w-0 items-center gap-1.5">
                <MapPinIcon className="h-4 w-4 shrink-0 text-slate-400" />
                <span className="truncate">{where}</span>
              </p>
            ) : null}
          </div>
          <div className="mt-auto pt-1">
            {registrationOpen(t) ? (
              <Badge tone="green" pulse>
                Registration open
              </Badge>
            ) : (
              <Badge tone="slate">
                {t.status === "complete" ? "Completed" : "Registration closed"}
              </Badge>
            )}
          </div>
        </div>
        <Scoreboard t={t} terms={terms} />
      </Card>
    </Link>
  );
}
